import React, { useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import { getDashboardDataAction } from "src/api/dashboard";

const SectionCard = ({ title, children }) => (
  <Card sx={{ mt: 3, borderRadius: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={600} mb={2}>
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const dispatch = useDispatch();

  const dashboardData =
    useSelector((state: any) => state.dashboard?.dashboardData) ||
    { result: {} };

  useEffect(() => {
    dispatch(getDashboardDataAction());
  }, []);

  const data = dashboardData?.result || {};

  return (
    <Box sx={{ mt: "30px", p: 3 }}>
      {/* KPI */}
      <Grid container spacing={2}>
        {[
          { label: "Schools", value: data.schools?.length || 0 },
          { label: "Cameras", value: data.cameras?.length || 0 },
          { label: "Parents", value: data.parents?.length || 0 },
          { label: "Captains", value: data.captains?.length || 0 },
          { label: "Bookings", value: data.bookings?.length || 0 },
        ].map((item, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography color="text.secondary" fontSize={14}>
                  {item.label}
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* SCHOOLS */}
      <SectionCard title="Schools">
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#e3f2fd" }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Pincode</TableCell>
                <TableCell>Landmark</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.schools?.map((s, i) => (
                <TableRow key={i}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.address}</TableCell>
                  <TableCell>{s.pincode}</TableCell>
                  <TableCell>{s.landmark || "-"}</TableCell>
                  <TableCell>{s.phone}</TableCell>
                  <TableCell>{s.email}</TableCell>
                  <TableCell>{s.createdOn}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* CAMERAS */}
      <SectionCard title="Cameras">
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#e3f2fd" }}>
              <TableRow>
                <TableCell>Camera No</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>SIM</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.cameras?.map((c, i) => (
                <TableRow key={i}>
                  <TableCell>{c.number}</TableCell>
                  <TableCell>{c.key}</TableCell>
                  <TableCell>{c.sim}</TableCell>
                  <TableCell>{c.driver}</TableCell>
                  <TableCell>
                    <Chip
                      label={c.status ? "Online" : "Offline"}
                      color={c.status ? "success" : "error"}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* PARENTS */}
      <SectionCard title="Parents">
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#e3f2fd" }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Child Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.parents?.map((p, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar>{p.name?.[0]}</Avatar>
                      {p.name}
                    </Box>
                  </TableCell>
                  <TableCell>{p.email}</TableCell>
                  <TableCell>{p.phone}</TableCell>
                  <TableCell>{p.childCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* CAPTAINS */}
      <SectionCard title="Captains">
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#e3f2fd" }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.captains?.map((c, i) => (
                <TableRow key={i}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.phone}</TableCell>
                  <TableCell>{c.createdAt}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* BOOKINGS */}
      <SectionCard title="Bookings">
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#e3f2fd" }}>
              <TableRow>
                <TableCell>Parent</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Child</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell>Emergency</TableCell>
                <TableCell>Emergency Phone</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.bookings?.map((b, i) => (
                <TableRow key={i}>
                  <TableCell>{b.parent}</TableCell>
                  <TableCell>{b.phone}</TableCell>
                  <TableCell>{b.child}</TableCell>
                  <TableCell>{b.grade}</TableCell>
                  <TableCell>{b.emergencyName}</TableCell>
                  <TableCell>{b.emergencyPhone}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );
}